/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN USER ACCOUNTS API
 * Role: Admin
 * Scope: Tenant-based user management (Hierarchy + Roles)
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */
const ADMIN_USER_ENDPOINT = "/admin/user-accounts/index.php";

/* =============================================================
   GET – Fetch User Accounts
   Supports: pagination, search
============================================================= */
export const getAdminUserAccounts = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_USER_ENDPOINT,
      null,
      "Admin – Fetch User Accounts",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch users",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };

  } catch (error) {
    console.error("Admin Fetch User Accounts Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};


/* =============================================================
   PUT – Enable / Disable User
   URL: /admin/user-accounts/index.php?id=ID
============================================================= */
export const updateAdminUserStatus = async (
  id,
  isActive,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid user role mapping ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_USER_ENDPOINT}?id=${id}`,  // ✅ user_org_roles.id
      { is_active: isActive ? 1 : 0 },
      "Admin – Update User Status",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to update user status",
      };
    }

    return {
      success: true,
      message: res.message || "User status updated successfully",
    };

  } catch (error) {
    console.error("Admin Update User Status Error:", error);

    return {
      success: false,
      message: error?.message || "Unexpected error occurred",
    };
  }
};


/* =============================================================
   GET – Fetch Single User Account (Optional Extension)
   URL: /admin/user-accounts/index.php?id=ID
============================================================= */
export const getAdminUserById = async (id, options = {}) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid user ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_USER_ENDPOINT}?id=${id}`,
      null,
      "Admin – Fetch Single User",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch user",
      };
    }

    return {
      success: true,
      data: res.data,
    };

  } catch (error) {
    console.error("Admin Fetch User By ID Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};
